import {
  AlertTriangle,
  CheckCircle2,
  CircleOff,
  Cpu,
  Download,
  FileVideo2,
  Gauge,
  LoaderCircle,
  RefreshCw,
  RotateCcw,
  Upload,
  X,
} from "lucide-react";
import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";

type Mode = "t2va" | "i2va" | "fl2va" | "l2va" | "ref2va";

type ControlJob = {
  id: string;
  chain_id: string | null;
  segment_index: number | null;
  segment_count: number | null;
  name: string;
  status: "queued" | "retry" | "leased" | "running" | "uploading" | "completed" | "failed";
  attempt: number;
  mode: Mode;
  profile: "turbo4" | "quality8";
  stage: string | null;
  elapsed_seconds: number | null;
  queue_position: number | null;
  result_available: boolean;
  error: string | null;
  created_at: number;
};

type Chain = {
  id: string;
  name: string;
  status: string;
  segment_count: number;
  completed_segments: number;
  result_available?: boolean;
};

type Worker = {
  worker_id: string;
  worker_version: string;
  state: string;
  comfy_ready: number;
  gpu: Record<string, unknown>;
  heartbeat_at: number;
  online: boolean;
};

type Snapshot = { jobs: ControlJob[]; chains: Chain[]; worker: Worker | null };

const MODES: Array<{ value: Mode; label: string; detail: string }> = [
  { value: "t2va", label: "T2VA", detail: "텍스트" },
  { value: "i2va", label: "I2VA", detail: "첫 프레임" },
  { value: "fl2va", label: "FL2VA", detail: "첫·끝" },
  { value: "l2va", label: "L2VA", detail: "끝 프레임" },
  { value: "ref2va", label: "Ref2VA", detail: "혼합 레퍼런스" },
];

const STATUS_LABELS: Record<ControlJob["status"], string> = {
  queued: "대기", retry: "재시도 대기", leased: "할당", running: "생성 중",
  uploading: "업로드", completed: "완료", failed: "실패",
};

async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`/api/control/v1${path}`, init);
  const body = await response.json().catch(() => ({})) as T & { error?: string };
  if (!response.ok) throw new Error(body.error || "요청을 처리하지 못했습니다.");
  return body;
}

function videoDuration(file: File): Promise<number> {
  return new Promise((resolve, reject) => {
    const video = document.createElement("video");
    const url = URL.createObjectURL(file);
    video.preload = "metadata";
    video.onloadedmetadata = () => {
      const duration = Math.round(video.duration * 1000) / 1000;
      URL.revokeObjectURL(url);
      if (duration < 2 || duration > 15) reject(new Error(`${file.name}: 레퍼런스 영상은 2–15초여야 합니다.`));
      else resolve(duration);
    };
    video.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error(`${file.name}: 영상 길이를 확인하지 못했습니다.`));
    };
    video.src = url;
  });
}

function elapsed(seconds: number | null): string {
  if (seconds == null) return "";
  const rounded = Math.max(0, Math.round(seconds));
  return rounded < 60 ? `${rounded}초` : `${Math.floor(rounded / 60)}분 ${rounded % 60}초`;
}

export function LocalH3ControlPlane({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [mode, setMode] = useState<Mode>("i2va");
  const [chain, setChain] = useState(false);
  const [snapshot, setSnapshot] = useState<Snapshot | null>(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const formRef = useRef<HTMLFormElement>(null);

  const refresh = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true);
    try {
      setSnapshot(await api<Snapshot>("/status"));
      setError("");
    } catch (reason) {
      if (!quiet) setError(reason instanceof Error ? reason.message : "상태를 불러오지 못했습니다.");
    } finally {
      if (!quiet) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    void refresh();
  }, [open, refresh]);

  const hasLiveWork = Boolean(snapshot?.jobs.some((job) =>
    ["leased", "running", "uploading"].includes(job.status),
  ));

  useEffect(() => {
    if (!open) return;
    let timer: number | undefined;
    let stopped = false;
    const schedule = () => {
      if (stopped || document.visibilityState !== "visible") return;
      timer = window.setTimeout(async () => {
        await refresh(true);
        schedule();
      }, hasLiveWork ? 5_000 : 15_000);
    };
    const handleVisibility = () => {
      if (timer !== undefined) window.clearTimeout(timer);
      timer = undefined;
      if (document.visibilityState === "visible") {
        void refresh(true);
        schedule();
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);
    schedule();
    return () => {
      stopped = true;
      if (timer !== undefined) window.clearTimeout(timer);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [hasLiveWork, open, refresh]);

  useEffect(() => {
    if (mode === "ref2va" && formRef.current) {
      const profile = formRef.current.elements.namedItem("profile") as HTMLSelectElement | null;
      if (profile) profile.value = "turbo4";
    }
  }, [mode]);

  const workerDetail = useMemo(() => {
    if (!snapshot?.worker) return "아직 heartbeat 없음";
    if (!snapshot.worker.online) return "마지막 heartbeat가 90초 이상 지났습니다";
    const gpu = snapshot.worker.gpu;
    const name = String(gpu.name || gpu.label || "RTX 3060 Ti");
    const utilization = gpu.utilization ?? gpu.utilization_percent;
    return `${name}${utilization != null ? ` · ${utilization}%` : ""} · ${snapshot.worker.state}`;
  }, [snapshot]);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const data = new FormData(event.currentTarget);
      data.set("mode", mode);
      data.set("chain_enabled", chain ? "true" : "false");
      const images = data.getAll("reference_images").filter((item): item is File => item instanceof File && item.size > 0);
      const videos = data.getAll("reference_videos").filter((item): item is File => item instanceof File && item.size > 0);
      const audios = data.getAll("reference_audios").filter((item): item is File => item instanceof File && item.size > 0);
      if (images.length > 9 || videos.length > 3 || audios.length > 3) {
        throw new Error("Ref2VA 입력은 이미지 9개, 영상 3개, 오디오 3개까지 사용할 수 있습니다.");
      }
      if (mode === "ref2va" && images.length + videos.length + audios.length === 0) {
        throw new Error("Ref2VA에는 이미지·영상·오디오 중 하나 이상이 필요합니다.");
      }
      const durations = await Promise.all(videos.map(videoDuration));
      durations.forEach((duration, index) => data.set(`reference_video_duration_${index}`, String(duration)));
      await api("/jobs", { method: "POST", body: data });
      event.currentTarget.reset();
      setMode("i2va");
      setChain(false);
      await refresh();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "작업을 제출하지 못했습니다.");
    } finally {
      setSubmitting(false);
    }
  }

  async function download(job: ControlJob) {
    try {
      const result = await api<{ download_url: string }>(`/jobs/${job.id}/download`);
      window.open(result.download_url, "_blank", "noopener,noreferrer");
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "결과를 열지 못했습니다.");
    }
  }

  async function retry(job: ControlJob) {
    try {
      await api(`/jobs/${job.id}/retry`, { method: "POST" });
      await refresh();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "재시도하지 못했습니다.");
    }
  }

  async function downloadChain(chainId: string) {
    try {
      const result = await api<{ download_url: string }>(`/chains/${chainId}/download`);
      window.open(result.download_url, "_blank", "noopener,noreferrer");
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "연속 영상을 열지 못했습니다.");
    }
  }

  if (!open) return null;

  return (
    <div className="control-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section className="control-plane" role="dialog" aria-modal="true" aria-labelledby="control-title">
        <header className="control-header">
          <div>
            <p className="eyebrow">LOCAL CONTROL PLANE</p>
            <h2 id="control-title">Windows · RTX 3060 Ti</h2>
          </div>
          <div className="control-header-actions">
            <button className="icon-button" onClick={onClose} aria-label="닫기"><X size={18} /></button>
          </div>
        </header>

        <div className={`worker-strip ${snapshot?.worker?.online ? "online" : "offline"}`}>
          {snapshot?.worker?.online ? <Cpu size={18} /> : <CircleOff size={18} />}
          <div><strong>{snapshot?.worker?.online ? "로컬 worker 온라인" : "로컬 worker 오프라인"}</strong><span>{workerDetail}</span></div>
          <button className="icon-button" onClick={() => void refresh()} aria-label="상태 새로고침" disabled={loading}>{loading ? <LoaderCircle className="spin" size={17} /> : <RefreshCw size={17} />}</button>
        </div>

        <div className="control-body">
          <form ref={formRef} className="local-submit" onSubmit={submit}>
            <div className="control-section-heading"><div><p className="eyebrow">FIXED GRAPH</p><h3>새 로컬 작업</h3></div><span>동시 실행 1</span></div>
            <fieldset className="local-mode-picker">
              <legend>생성 모드</legend>
              {MODES.map((item) => (
                <label key={item.value}>
                  <input type="radio" name="mode_picker" value={item.value} checked={mode === item.value} onChange={() => setMode(item.value)} />
                  <strong>{item.label}</strong><small>{item.detail}</small>
                </label>
              ))}
            </fieldset>
            <div className="local-form-grid">
              <label>작업 이름<input name="name" required maxLength={100} defaultValue="3060 Ti 작업" /></label>
              <label>프로필<select name="profile" defaultValue="turbo4"><option value="turbo4">turbo4 · 4-step</option>{mode !== "ref2va" && <option value="quality8">quality8 · 8-step</option>}</select></label>
            </div>
            <label>H3 프롬프트<textarea name="prompt" required rows={7} maxLength={49152} placeholder="카메라, 동작, 시간대별 연출, continuity와 오디오 지시를 입력하세요." /></label>

            {(mode === "i2va" || mode === "fl2va") && <label className="file-field"><span>첫 프레임 · JPEG/PNG/WebP</span><input name="first_frame" type="file" required accept="image/jpeg,image/png,image/webp" /></label>}
            {(mode === "l2va" || mode === "fl2va") && <label className="file-field"><span>마지막 프레임 · JPEG/PNG/WebP</span><input name="last_frame" type="file" required accept="image/jpeg,image/png,image/webp" /></label>}
            {mode === "ref2va" && (
              <div className="reference-inputs">
                <label className="file-field"><span>Picture · 최대 9개</span><input name="reference_images" type="file" multiple accept="image/jpeg,image/png,image/webp" /></label>
                <label className="file-field"><span>Video · 최대 3개, 각 2–15초</span><input name="reference_videos" type="file" multiple accept="video/mp4,video/webm,video/quicktime,video/x-m4v" /></label>
                <label className="file-field"><span>Audio · 최대 3개</span><input name="reference_audios" type="file" multiple accept="audio/wav,audio/mpeg,audio/mp4,audio/flac,audio/ogg" /></label>
              </div>
            )}

            <div className="local-form-grid compact-grid">
              <label>가로<input name="width" type="number" min={256} max={4096} step={32} defaultValue={960} required /></label>
              <label>세로<input name="height" type="number" min={256} max={4096} step={32} defaultValue={544} required /></label>
              <label>프레임<input name="frames" type="number" min={5} max={362} step={17} defaultValue={124} required /></label>
              <label>시드<input name="seed" inputMode="numeric" pattern="[0-9]*" placeholder="자동" /></label>
            </div>

            <label className="chain-toggle"><input type="checkbox" checked={chain} onChange={(event) => setChain(event.target.checked)} /><span><strong>3-segment 연속 영상</strong><small>각 완료 PNG를 다음 첫 프레임으로 사용한 뒤 순서대로 합칩니다.</small></span></label>
            {chain && (
              <div className="chain-fields">
                <label>Continuity constraints<textarea name="continuity_constraints" required rows={3} placeholder="마스크 착용 여부, 인물 외형, 의상, 배경을 명시하세요." /></label>
                <label>Segment 2 프롬프트<textarea name="segment_2_prompt" required rows={4} /></label>
                <label>Segment 3 프롬프트<textarea name="segment_3_prompt" required rows={4} /></label>
                {mode === "ref2va" && <><input type="hidden" name="segment_2_profile" value="turbo4" /><input type="hidden" name="segment_3_profile" value="turbo4" /></>}
              </div>
            )}
            <button className="button primary local-submit-button" disabled={submitting}>{submitting ? <LoaderCircle className="spin" size={17} /> : <Upload size={17} />}{submitting ? "R2 업로드 중" : "3060 Ti 대기열에 넣기"}</button>
          </form>

          <section className="local-queue">
            <div className="control-section-heading"><div><p className="eyebrow">SERIAL QUEUE</p><h3>작업 상태</h3></div><span>{snapshot?.jobs.length || 0}개</span></div>
            <div className="local-job-list">
              {snapshot?.jobs.map((job) => {
                const chainState = job.chain_id ? snapshot.chains.find((item) => item.id === job.chain_id) : null;
                return (
                  <article className={`local-job status-${job.status}`} key={job.id}>
                    <div className="local-job-icon">{job.status === "completed" ? <CheckCircle2 size={18} /> : job.status === "failed" ? <AlertTriangle size={18} /> : job.status === "running" || job.status === "uploading" ? <Gauge size={18} /> : <FileVideo2 size={18} />}</div>
                    <div className="local-job-copy">
                      <div><strong>{job.name}</strong><span className={`local-status ${job.status}`}>{STATUS_LABELS[job.status]}</span></div>
                      <p>{job.mode.toUpperCase()} · {job.profile}{job.queue_position ? ` · 대기 ${job.queue_position}번` : ""}{job.elapsed_seconds != null ? ` · ${elapsed(job.elapsed_seconds)}` : ""}</p>
                      {chainState && <div className="chain-progress"><span style={{ width: `${Math.round((chainState.completed_segments / chainState.segment_count) * 100)}%` }} /><small>연속 영상 {chainState.completed_segments}/{chainState.segment_count} · {chainState.status}</small></div>}
                      {job.error && <p className="local-job-error">{job.error}</p>}
                    </div>
                    <div className="local-job-actions">
                      {job.result_available && <button className="icon-button" onClick={() => void download(job)} aria-label={`${job.name} 다운로드`}><Download size={17} /></button>}
                      {chainState?.result_available && job.segment_index === job.segment_count && <button className="icon-button" onClick={() => void downloadChain(chainState.id)} aria-label={`${chainState.name} 결합본 다운로드`}><FileVideo2 size={17} /></button>}
                      {job.status === "failed" && <button className="icon-button" onClick={() => void retry(job)} aria-label={`${job.name} 재시도`}><RotateCcw size={17} /></button>}
                    </div>
                  </article>
                );
              })}
              {!snapshot?.jobs.length && <div className="local-empty"><FileVideo2 size={24} /><strong>로컬 작업이 없습니다</strong><p>왼쪽 폼에서 고정 H3 graph 작업을 제출하세요.</p></div>}
            </div>
          </section>
        </div>
        {error && <div className="control-error" role="alert"><AlertTriangle size={16} /><span>{error}</span></div>}
      </section>
    </div>
  );
}
